// Auswertung der Rollenverfolgung (Verwaltung → Rollen, Kundenwunsch 27.10.2026). Eingang: Antwort von
// ticketsale?option=rollTracking (bconn-aws, ticketsale-get):
//   devices – je Drucker mit Verkäufen der letzten 30 Tage: sales, gebe_sales, roll_sales, gebe_without_roll,
//             printer_types ('GEBE', 'SUNMI', … kommagetrennt), comment, last_sale (Ortszeit), last_upload (UTC)
//   sales   – Verkäufe der GeBE-Drucker der letzten 120 Tage, aufsteigend: printer_id, sale_time (Ortszeit, Text),
//             roll_numbers (je 10 Ziffern: 6 Rolle, 4 Abschnitt) oder null, paper_feed_mm, printer_serial, has_log,
//             is_cancellation, username, ticket_name, upload_id
//   prints  – (ab 05.11.2026, Kunde Kalb; fehlt bei älterem Backend) Druckprotokoll der App (print_log) der letzten
//             120 Tage, aufsteigend: print_log_id, printer_id, user_id, username, print_type, description, print_time
//             (Ortszeit, Text), roll_numbers, paper_feed_mm, printer_serial, has_log, autoload_fail, print_message.
//             Arten und Etiketten in src/components/printlog/printKinds.js (gemeinsam mit dem Druckprotokoll).
//             Nur die feste Liste TESTDRUCK, ABRECHNUNG, FAHRPLAN_LINIE, FAHRPLAN_HALTESTELLE, DIENSTPLAN, DOKUMENT =
//             Druck ohne Verkauf: belegt Abschnitte wie ein Verkauf (zählt aber nicht als Verkauf), ohne Rollennummer
//             erklärt er eine Lücke wie ein Verkauf ohne Rollennummer. VERBINDUNG (Drucker verbunden) und PAPIEREINZUG =
//             Positionsmeldung: druckt und belegt nichts; roll_numbers (meist genau eine) ist die Stelle an der
//             Druckposition, schon um den Scanner-Versatz korrigiert. Daraus: Einlage der Rolle (roll.inserted),
//             Erklärung von Lücken, wenn Papier ohne Druck vorgeschoben wurde, Warnung bei gemeldet fehlgeschlagenem
//             Papiereinzug (autoload_fail). Alle übrigen Arten – Druckerzustände der App ab 3.0.6 (DECKEL_OFFEN,
//             DECKEL_OEFFNEN, PAPIER_LEER, STOERUNG, GETRENNT; verbrauchen kein Papier, meist ohne Rollennummer) und
//             künftige unbekannte – ignoriert die Rollenauswertung (bis 3.0.14 galt jede Art außer den Positions-
//             meldungen als Druck ohne Verkauf und hätte Lücken erklärt).
// Ein MVV-Abschnitt ist 1 Zoll (25,4 mm) lang, ein Ticket belegt meist 2–3 Abschnitte; aufeinanderfolgende Tickets
// teilen sich den Grenzabschnitt (mehrfach belegte Abschnitte sind deshalb normal und keine Warnung). Die Abschnitte
// zählen aufwärts ab etwa 0. Lücke = Abschnitt zwischen kleinstem und größtem belegten Abschnitt einer Rolle, den kein
// Verkauf und kein Druck ohne Verkauf belegt; erklärt, wenn auf dem Drucker zwischen den beiden Drucken um die Lücke
// 1. eine Positionsmeldung derselben Rolle hinter dem letzten belegten Abschnitt lag (Papier ohne Druck vorgeschoben),
// 2. ein Verkauf oder Druck ohne erkannte Rollennummer lag (der Abschnitt ist dann sehr wahrscheinlich dessen Papier)
// oder 3. eine Positionsmeldung ohne gelesene Stelle (alte Firmware liest in Ruhe nicht). Eine Lücke von genau einem
// Abschnitt ist unkritisch (Kundenhinweis 27.10.2026: „aus einem abschnitt lässt sich kein ticket drucken“; meist nur
// nicht erkannt) – keine Warnung, nicht in den Kennzahlen, im Band grau. Der MVV verlangt für Papierlücken den
// Nachweis (Handlungsanweisung Fahrkarten, Anlage A 10, Abschnitt 6).

import {
  PRINT_TYPES,
  POSITION_TYPES,
  isPaperPrint as isPaperPrintKind,
  isPosition as isPositionKind
} from '../printlog/printKinds';

export const SECTION_MM = 25.4;
// bis Rollen vollständig verbraucht wurden: MVV-Papierdokument 700 Abschnitte, gemessen rund 750–780
export const FALLBACK_CAPACITY = 750;
// nur so weit verbrauchte Rollen zählen zum Lernen der Kapazität (früher beendete Rollen sind Reste)
const LEARN_MIN_SECTION = 600;
// so nah an der Kapazität gilt eine beendete Rolle als vollständig verbraucht
const FULL_MARGIN = 15;
// Anteil der GeBE-Verkäufe ohne Rollennummer, ab dem ein Gerät rot statt gelb ist
const ERROR_SHARE = 0.25;

const ROLL_NUMBER = /^\d{10}$/;

// Drucke ohne Verkauf (Papier) und Positionsmeldungen: Etiketten aus printKinds.js, hier weiter exportiert
export { PRINT_TYPES, POSITION_TYPES };

// Positionsmeldung (VERBINDUNG, PAPIEREINZUG): druckt nichts, meldet nur die Stelle der Rolle
function isPosition(print) {
  return isPositionKind(print.print_type);
}

// Druck ohne Verkauf, der Papier verbraucht (feste Liste PRINT_TYPES)
function isPaperPrint(print) {
  return isPaperPrintKind(print.print_type);
}

// Art eines Drucks ohne Verkauf, mit Beschreibung: „Fahrplan (Linie) · Linie 101“
export function printText(print) {
  const type = PRINT_TYPES[print.print_type] || POSITION_TYPES[print.print_type] || 'Druck';
  return print.description ? type + ' · ' + print.description : type;
}

// Einlage einer Rolle (roll.inserted) ohne Zeit und Drucker, nach „Eingelegt“: „bei Abschnitt 3 (Papiereinzug)“
export function insertedText(inserted) {
  return 'bei Abschnitt ' + inserted.section + ' (' + (POSITION_TYPES[inserted.type] || inserted.type) + ')';
}

function parseRollNumber(value) {
  const text = String(value || '').trim();
  if (!ROLL_NUMBER.test(text)) {
    return null;
  }
  return { roll: text.slice(0, 6), section: Number(text.slice(6)) };
}

// zusammenhängende Bereiche aus einer sortierten Liste von Zahlen: [3,4,5,9] → [[3,5],[9,9]]
export function toRanges(sorted) {
  const ranges = [];
  sorted.forEach((n) => {
    const last = ranges[ranges.length - 1];
    if (last && n === last[1] + 1) {
      last[1] = n;
    } else if (!last || n > last[1]) {
      ranges.push([n, n]);
    }
  });
  return ranges;
}

export function rangeText(a, b) {
  return a === b ? String(a) : a + '–' + b;
}

function printerTypesOf(device) {
  return String(device.printer_types || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

// Gerätestatus aus den Kennzahlen der letzten 30 Tage
export function deviceState(device) {
  const types = printerTypesOf(device);
  const gebe = Number(device.gebe_sales) || 0;
  const missing = Number(device.gebe_without_roll) || 0;
  if (!gebe) {
    return {
      level: 'none',
      text: types.length ? 'Ohne Rollenverfolgung' : 'Druckertyp unbekannt',
      detail: types.length ? 'Drucker: ' + types.join(', ') : 'App meldet keinen Druckertyp (ältere Version)'
    };
  }
  const other = types.filter((t) => t !== 'GEBE');
  const extra = other.length ? 'zeitweise ohne GeBE-Drucker (' + other.join(', ') + ')' : null;
  if (!missing) {
    return { level: 'ok', text: 'Rollennummern okay', detail: extra || gebe + ' von ' + gebe + ' mit Rollennummer' };
  }
  if (missing >= gebe) {
    return { level: 'error', text: 'Keine Rollennummern', detail: missing + ' von ' + gebe + ' ohne Rollennummer' };
  }
  return {
    level: missing / gebe >= ERROR_SHARE ? 'error' : 'warn',
    text: missing + ' von ' + gebe + ' ohne Rollennummer',
    detail: extra || 'Rollennummer nicht erkannt (Diagnose im Verkauf)'
  };
}

const LEVEL_ORDER = { error: 0, warn: 1, ok: 2, none: 3 };

// Hauptauswertung. now: Date (für das 30-Tage-Fenster der Rollen); Zeiten der Verkäufe sind Ortszeit-Texte und werden
// als lokale Zeit gelesen.
export function analyzeRolls(data, now = new Date()) {
  const windowDays = Number(data && data.window_days) || 30;
  const windowStart = new Date(now.getTime() - windowDays * 86400000);
  // nur Drucke ohne Verkauf (Papier) und Positionsmeldungen; Druckerzustände und unbekannte Arten bleiben außen vor
  const prints = ((data && data.prints) || []).filter((p) => isPaperPrint(p) || isPosition(p));
  // Verkäufe und Drucke ohne Verkauf in Zeitfolge (bei gleicher Zeit Verkäufe zuerst)
  const items = ((data && data.sales) || []).map((sale) => ({
    sale, printer: sale.printer_id, time: sale.sale_time, numbers: sale.roll_numbers, rank: 0, id: sale.upload_id || 0
  })).concat(prints.filter(isPaperPrint).map((print) => ({
    print, printer: print.printer_id, time: print.print_time, numbers: print.roll_numbers, rank: 1,
    id: print.print_log_id || 0
  }))).sort((x, y) => String(x.time).localeCompare(String(y.time)) || x.rank - y.rank || x.id - y.id);

  // 1. Ereignisse je Rolle und Rollenfolge je Drucker
  const rolls = new Map();
  const printerRolls = new Map(); // printer → [{ roll, time }] in Zeitfolge
  const noRoll = new Map(); // printer → [time] der Verkäufe ohne Rollennummer
  const noRollPrints = new Map(); // printer → [Druck] der Drucke ohne Verkauf und ohne Rollennummer
  items.forEach((item) => {
    const printer = item.printer;
    const parsed = (item.numbers || []).map(parseRollNumber).filter(Boolean);
    if (!parsed.length) {
      const target = item.sale ? noRoll : noRollPrints;
      if (!target.has(printer)) {
        target.set(printer, []);
      }
      target.get(printer).push(item.sale ? item.time : item.print);
      return;
    }
    const byRoll = new Map();
    parsed.forEach((p) => {
      if (!byRoll.has(p.roll)) {
        byRoll.set(p.roll, []);
      }
      byRoll.get(p.roll).push(p.section);
    });
    const source = item.sale || item.print;
    byRoll.forEach((sections, rollId) => {
      if (!rolls.has(rollId)) {
        rolls.set(rollId, {
          id: rollId, events: [], sections: new Set(), saleSections: new Set(), printers: [], serials: []
        });
      }
      const roll = rolls.get(rollId);
      const sorted = sections.slice().sort((a, b) => a - b);
      // sale bei Verkäufen, print bei Drucken ohne Verkauf
      roll.events.push({
        sale: item.sale, print: item.print, printer, time: item.time,
        sections: sorted, min: sorted[0], max: sorted[sorted.length - 1]
      });
      sorted.forEach((s) => roll.sections.add(s));
      if (item.sale) {
        sorted.forEach((s) => roll.saleSections.add(s));
      }
      if (roll.printers.indexOf(printer) === -1) {
        roll.printers.push(printer);
      }
      if (source.printer_serial && roll.serials.indexOf(source.printer_serial) === -1) {
        roll.serials.push(source.printer_serial);
      }
      if (!printerRolls.has(printer)) {
        printerRolls.set(printer, []);
      }
      printerRolls.get(printer).push({ roll: rollId, time: item.time });
    });
  });

  // Positionsmeldungen je Drucker in Zeitfolge (Rolle und Abschnitt der ersten gelesenen Nummer, sonst null)
  const positions = new Map(); // printer → [{ time, printer, type, roll, section, autoloadFail }]
  prints.filter(isPosition).forEach((print) => {
    const read = (print.roll_numbers || []).map(parseRollNumber).filter(Boolean)[0] || null;
    if (!positions.has(print.printer_id)) {
      positions.set(print.printer_id, []);
    }
    positions.get(print.printer_id).push({
      time: print.print_time,
      printer: print.printer_id,
      type: print.print_type,
      roll: read ? read.roll : null,
      section: read ? read.section : null,
      autoloadFail: !!print.autoload_fail
    });
  });
  positions.forEach((list) => list.sort((x, y) => String(x.time).localeCompare(String(y.time))));
  const inserted = insertions(positions, printerRolls);

  // aktuelle Rolle je Drucker = Rolle des letzten Verkaufs oder Drucks mit Rollennummer (Positionsmeldungen nicht)
  const currentRoll = new Map();
  printerRolls.forEach((list, printer) => currentRoll.set(printer, list[list.length - 1].roll));

  // 2. Kennzahlen je Rolle
  const list = [];
  rolls.forEach((roll) => {
    const sorted = Array.from(roll.sections).sort((a, b) => a - b);
    // nur von Drucken ohne Verkauf belegte Abschnitte (im Band abgesetzt)
    const printOnly = sorted.filter((n) => !roll.saleSections.has(n));
    const first = roll.events[0];
    const last = roll.events[roll.events.length - 1];
    const activeOn = roll.printers.filter((p) => currentRoll.get(p) === roll.id);
    list.push({
      id: roll.id,
      events: roll.events,
      printers: roll.printers,
      serials: roll.serials,
      activeOn,
      status: activeOn.length ? 'active' : 'finished',
      min: sorted[0],
      max: sorted[sorted.length - 1],
      covered: sorted.length,
      ranges: toRanges(sorted),
      printRanges: toRanges(printOnly),
      firstTime: first.time,
      lastTime: last.time,
      saleCount: new Set(roll.events.filter((e) => e.sale).map((e) => e.sale.upload_id)).size,
      printCount: new Set(roll.events.filter((e) => e.print).map((e) => e.print.print_log_id)).size,
      inserted: inserted.get(roll.id) || null,
      gaps: [],
      warnings: []
    });
  });

  // 3. Kapazität aus vollständig verbrauchten Rollen lernen
  const learnedFrom = list.filter((r) => r.status === 'finished' && r.max >= LEARN_MIN_SECTION);
  const learned = learnedFrom.length ? Math.max.apply(null, learnedFrom.map((r) => r.max)) : null;
  const capacity = {
    value: learned || FALLBACK_CAPACITY,
    learned: !!learned,
    basis: learnedFrom.length
  };

  // 4. Lücken mit Erklärung, Warnungen, Rest
  list.forEach((roll) => {
    for (let i = 0; i < roll.ranges.length - 1; i++) {
      const from = roll.ranges[i][1] + 1;
      const to = roll.ranges[i + 1][0] - 1;
      // Verkauf vor und nach der Lücke (in Abschnittsfolge)
      const before = roll.events.filter((e) => e.max < from).sort((x, y) => y.max - x.max || String(y.time).localeCompare(String(x.time)))[0];
      const after = roll.events.filter((e) => e.min > to).sort((x, y) => x.min - y.min || String(x.time).localeCompare(String(y.time)))[0];
      let explained = false;
      let reason = prints.length ? 'Kein Verkauf oder Druck zu diesen Abschnitten' : 'Kein Verkauf zu diesen Abschnitten';
      if (before && after) {
        const lo = String(before.time) < String(after.time) ? before.time : after.time;
        const hi = String(before.time) < String(after.time) ? after.time : before.time;
        const printers = before.printer === after.printer ? [before.printer] : [before.printer, after.printer];
        const inside = (t) => t > lo && t < hi;
        const between = printers.reduce((n, p) => n + (noRoll.get(p) || []).filter(inside).length, 0);
        const printsBetween = printers.reduce((list, p) =>
          list.concat((noRollPrints.get(p) || []).filter((d) => inside(d.print_time))), []);
        const moved = printers.reduce((list, p) =>
          list.concat((positions.get(p) || []).filter((m) => inside(m.time))), []);
        // Vorrang: Papier ohne Druck vorgeschoben > Verkauf/Druck ohne Rollennummer > Position nicht gelesen.
        // Eine Positionsmeldung nennt den Abschnitt, mit dem der nächste Druck beginnt: Sie erklärt nur die Abschnitte
        // davor, die Lücke also nur, wenn sie bis an deren Ende reicht (bis auf den gemeinsamen Grenzabschnitt).
        const advanced = moved.filter((m) => m.roll === roll.id && m.section > before.max)
          .sort((x, y) => String(x.time).localeCompare(String(y.time))).pop();
        const how = advanced && (advanced.type === 'VERBINDUNG' ? 'Drucker neu verbunden' : 'Papiereinzug');
        if (advanced && advanced.section >= to) {
          explained = true;
          reason = 'Papier beim Einlegen vorgeschoben (' + how + ' bei Abschnitt ' + advanced.section + ')';
        } else if (between || printsBetween.length) {
          explained = true;
          reason = noRollReason(between, printsBetween);
        } else if (advanced) {
          reason = 'Papier beim Einlegen nur bis Abschnitt ' + advanced.section + ' vorgeschoben (' + how +
            '), Abschnitte ' + rangeText(advanced.section, to) + ' ohne Druck';
        } else if (moved.some((m) => m.roll === null)) {
          explained = true;
          reason = 'Drucker zwischendurch neu verbunden/Papier eingelegt (Position nicht gelesen)';
        } else if (before.printer !== after.printer) {
          reason = 'Rolle wechselte von Drucker ' + before.printer + ' zu ' + after.printer;
        }
      }
      const count = to - from + 1;
      // ein einzelner Abschnitt reicht für kein Ticket: unkritisch, sofern nicht ohnehin erklärt
      const minor = !explained && count === 1;
      if (minor) {
        reason = 'Einzelner Abschnitt – daraus lässt sich kein Ticket drucken (vermutlich nicht erkannt)';
      }
      roll.gaps.push({ from, to, count, explained, minor, reason, before, after });
    }
    roll.unexplained = roll.gaps.filter((g) => !g.explained && !g.minor);
    roll.minorGaps = roll.gaps.filter((g) => g.minor);
    roll.unexplainedSections = roll.unexplained.reduce((n, g) => n + g.count, 0);
    roll.explainedSections = roll.gaps.filter((g) => g.explained).reduce((n, g) => n + g.count, 0);

    const effectiveCapacity = Math.max(capacity.value, roll.max);
    roll.capacity = effectiveCapacity;
    roll.rest = roll.status === 'active' ? Math.max(0, effectiveCapacity - roll.max) : 0;
    roll.restShare = roll.status === 'active' ? roll.rest / effectiveCapacity : 0;
    roll.full = roll.status === 'finished' && roll.max >= capacity.value - FULL_MARGIN;

    if (roll.unexplained.length) {
      roll.warnings.push({
        level: 'error',
        text: roll.unexplained.length === 1
          ? 'Lücke bei Abschnitt ' + rangeText(roll.unexplained[0].from, roll.unexplained[0].to)
          : roll.unexplained.length + ' Lücken (' + roll.unexplainedSections + ' Abschnitte)'
      });
    }
    if (roll.status === 'active' && roll.restShare < 0.08) {
      roll.warnings.push({ level: 'info', text: 'Fast leer – noch etwa ' + roll.rest + ' Abschnitte' });
    }
    if (roll.saleCount === 1 && !roll.printCount && roll.covered <= 2) {
      roll.warnings.push({ level: 'warn', text: 'Nur ein Verkauf – Rollennummer falsch gelesen?' });
    }
    const failed = [];
    positions.forEach((list) => list.forEach((m) => {
      if (m.autoloadFail && m.roll === roll.id) {
        failed.push(m.section);
      }
    }));
    if (failed.length) {
      roll.warnings.push({
        level: 'warn',
        text: failed.length === 1
          ? 'Papiereinzug gemeldet als fehlgeschlagen (Abschnitt ' + failed[0] + ')'
          : 'Papiereinzug ' + failed.length + '× gemeldet als fehlgeschlagen (Abschnitte ' + failed.join(', ') + ')'
      });
    }
    if (roll.status === 'finished' && !roll.full && roll.saleCount + roll.printCount > 1) {
      roll.warnings.push({ level: 'info', text: 'Beendet bei Abschnitt ' + roll.max + ' (Rest zurückgegeben?)' });
    }
    if (roll.printers.length > 1) {
      roll.warnings.push({ level: 'info', text: 'In ' + roll.printers.length + ' Druckern: ' + roll.printers.join(', ') });
    }
    if (roll.serials.length > 1 && roll.printers.length === 1) {
      roll.warnings.push({ level: 'info', text: 'Drucker getauscht (' + roll.serials.length + ' Seriennummern)' });
    }
    roll.level = roll.warnings.some((w) => w.level === 'error') ? 'error'
      : roll.warnings.some((w) => w.level === 'warn') ? 'warn' : 'ok';
  });

  // 5. Fenster: nur Rollen mit Verkäufen in den letzten 30 Tagen
  const inWindow = (roll) => toDate(roll.lastTime) >= windowStart;
  const active = list.filter((r) => r.status === 'active' && inWindow(r))
    .sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] || a.restShare - b.restShare);
  const finished = list.filter((r) => r.status === 'finished' && inWindow(r))
    .sort((a, b) => String(b.lastTime).localeCompare(String(a.lastTime)));

  // 6. Geräte (30 Tage) mit Status und aktueller Rolle
  const rollById = new Map(list.map((r) => [r.id, r]));
  const devices = ((data && data.devices) || []).map((device) => {
    const state = deviceState(device);
    const rollId = currentRoll.get(device.printer_id);
    return Object.assign({}, device, {
      state,
      currentRoll: rollId && rollById.get(rollId) && inWindow(rollById.get(rollId)) ? rollById.get(rollId) : null
    });
  }).sort((a, b) => LEVEL_ORDER[a.state.level] - LEVEL_ORDER[b.state.level] || a.printer_id - b.printer_id);

  const tracked = devices.filter((d) => d.state.level !== 'none');
  const gebeSales = tracked.reduce((n, d) => n + (Number(d.gebe_sales) || 0), 0);
  const withoutRoll = tracked.reduce((n, d) => n + (Number(d.gebe_without_roll) || 0), 0);
  const shown = active.concat(finished);
  const kpis = {
    devicesTracked: tracked.length,
    devicesOk: tracked.filter((d) => d.state.level === 'ok').length,
    devicesWarn: tracked.filter((d) => d.state.level === 'warn').length,
    devicesError: tracked.filter((d) => d.state.level === 'error').length,
    devicesUntracked: devices.length - tracked.length,
    activeRolls: active.length,
    finishedRolls: finished.length,
    unexplainedGaps: shown.reduce((n, r) => n + r.unexplained.length, 0),
    unexplainedSections: shown.reduce((n, r) => n + r.unexplainedSections, 0),
    explainedGaps: shown.reduce((n, r) => n + r.gaps.filter((g) => g.explained).length, 0),
    minorGaps: shown.reduce((n, r) => n + r.minorGaps.length, 0),
    gebeSales,
    withoutRoll
  };

  return { windowDays, capacity, devices, active, finished, kpis, all: list };
}

// Einlage je Rolle: früheste Positionsmeldung mit dieser Rolle, vor der auf demselben Drucker zuletzt eine andere
// Rolle (Positionsmeldung oder Druck) oder noch gar keine gemeldet war. Map Rolle → { section, time, printer, type }
function insertions(positions, printerRolls) {
  const found = new Map();
  positions.forEach((list, printer) => {
    // Beobachtungen mit Rolle in Zeitfolge, bei gleicher Zeit die Positionsmeldung zuerst (gelesen vor dem Druck)
    const seen = list.filter((m) => m.roll !== null).map((m) => ({ roll: m.roll, time: m.time, rank: 0, position: m }))
      .concat((printerRolls.get(printer) || []).map((e) => ({ roll: e.roll, time: e.time, rank: 1 })))
      .sort((x, y) => String(x.time).localeCompare(String(y.time)) || x.rank - y.rank);
    seen.forEach((obs, i) => {
      const prev = seen[i - 1];
      if (!obs.position || (prev && prev.roll === obs.roll)) {
        return;
      }
      const known = found.get(obs.roll);
      if (!known || String(obs.time) < String(known.time)) {
        found.set(obs.roll, { section: obs.position.section, time: obs.time, printer, type: obs.position.type });
      }
    });
  });
  return found;
}

// Begründung für Verkäufe (Anzahl) und Drucke ohne Verkauf (Liste) ohne Rollennummer zwischen zwei Drucken
function noRollReason(sales, prints) {
  const parts = [];
  if (sales) {
    parts.push(sales === 1 ? 'Verkauf' : sales + ' Verkäufe');
  }
  if (prints.length) {
    parts.push(prints.length === 1 ? (PRINT_TYPES[prints[0].print_type] || 'Druck') : prints.length + ' Drucke');
  }
  return parts.join(' und ') + ' ohne Rollennummer dazwischen';
}

// Ortszeit-Text „2026-10-27T07:13:34“ als lokale Zeit
export function toDate(text) {
  if (!text) {
    return null;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(text));
  if (!m) {
    return new Date(text);
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] || 0));
}

// verbleibende Verkäufe grob: Rest durch den bisherigen Verbrauch je Druck dieser Rolle (Spanne der belegten
// Abschnitte je Verkauf oder Druck ohne Verkauf; Tickets teilen sich den Grenzabschnitt, daher nicht die Zahl der
// Abschnitte je Ticket)
export function ticketsLeft(roll) {
  const count = roll ? roll.saleCount + (roll.printCount || 0) : 0;
  if (!roll || roll.status !== 'active' || count < 3) {
    return null;
  }
  const perSale = (roll.max - roll.min + 1) / count;
  return perSale > 0 ? Math.floor(roll.rest / perSale) : null;
}
